import React, { useState, useEffect } from 'react';
import { employeeData, availableLocations, availableDepartments, TAX_RATE, BENEFITS_RATE } from '../data/employees';
import { Employee } from '../types';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Trash2, Plus, Edit, X, Check, AlertCircle, Save, DollarSign, Users, Filter } from 'lucide-react';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#ff6b6b'];
const STORAGE_KEY = 'financialDashboard_employees';

const PayrollCalculator: React.FC = () => {
  // Load employees from localStorage or use default data
  const loadEmployeesFromStorage = (): Employee[] => {
    const savedEmployees = localStorage.getItem(STORAGE_KEY);
    if (savedEmployees) {
      try {
        return JSON.parse(savedEmployees);
      } catch (error) {
        console.error('Error parsing saved employees:', error);
        return employeeData;
      }
    }
    return employeeData;
  };

  const [employees, setEmployees] = useState<Employee[]>(loadEmployeesFromStorage);
  const [selectedLocation, setSelectedLocation] = useState<string>('all');
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  const [showInactive, setShowInactive] = useState<boolean>(false);
  const [isAddingEmployee, setIsAddingEmployee] = useState<boolean>(false);
  const [isEditingEmployee, setIsEditingEmployee] = useState<boolean>(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState<number | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [payType, setPayType] = useState<'hourly' | 'salary'>('hourly');
  
  // Form state for new employee
  const [newEmployee, setNewEmployee] = useState<Partial<Employee>>({
    name: '',
    location: availableLocations[0],
    department: availableDepartments[0],
    isActive: true,
    hourlyRate: null,
    hoursWorked: null,
    salary: null,
    calculatedPay: 0,
    taxes: 0,
    totalPay: 0
  });
  
  // Filter employees based on selected location, department and active status
  const filteredEmployees = employees.filter(employee => {
    const locationMatch = selectedLocation === 'all' || employee.location === selectedLocation;
    const departmentMatch = selectedDepartment === 'all' || employee.department === selectedDepartment;
    const activeMatch = showInactive || employee.isActive;
    return locationMatch && departmentMatch && activeMatch;
  });
  
  // Calculate total payroll
  const totalPayroll = filteredEmployees.reduce((sum, employee) => sum + employee.calculatedPay, 0);
  const totalTaxes = filteredEmployees.reduce((sum, employee) => sum + employee.taxes, 0);
  const totalPay = filteredEmployees.reduce((sum, employee) => sum + employee.totalPay, 0);
  
  // Save employees to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(employees));
  }, [employees]);
  
  // Reset form when adding new employee
  const resetForm = () => {
    setNewEmployee({
      name: '',
      location: availableLocations[0],
      department: availableDepartments[0],
      isActive: true,
      hourlyRate: null,
      hoursWorked: null,
      salary: null,
      calculatedPay: 0,
      taxes: 0,
      totalPay: 0
    });
    setPayType('hourly');
    setFormError(null);
  };
  
  // Calculate pay based on hourly rate and hours worked or salary
  const calculatePay = (employee: Partial<Employee>): { calculatedPay: number, taxes: number, totalPay: number } => {
    let calculatedPay = 0;
    
    if (employee.hourlyRate !== null && employee.hoursWorked !== null) {
      calculatedPay = employee.hourlyRate * employee.hoursWorked;
    } else if (employee.salary !== null) {
      calculatedPay = employee.salary;
    }
    
    const taxes = calculatedPay * TAX_RATE;
    const benefits = calculatedPay * BENEFITS_RATE;
    const totalPay = calculatedPay + benefits - taxes;
    
    return { calculatedPay, taxes, totalPay };
  };
  
  // Handle adding a new employee
  const handleAddEmployee = () => {
    // Validate form
    if (!newEmployee.name || newEmployee.name.trim() === '') {
      setFormError('Employee name is required');
      return;
    }
    
    if (payType === 'hourly') {
      if (!newEmployee.hourlyRate || newEmployee.hourlyRate <= 0) {
        setFormError('Hourly rate must be greater than 0');
        return;
      }
      
      if (!newEmployee.hoursWorked || newEmployee.hoursWorked <= 0) {
        setFormError('Hours worked must be greater than 0');
        return;
      }
    } else {
      if (!newEmployee.salary || newEmployee.salary <= 0) {
        setFormError('Salary must be greater than 0');
        return;
      }
    }
    
    // Calculate pay
    const { calculatedPay, taxes, totalPay } = calculatePay(newEmployee);
    
    // Create new employee with unique ID
    const maxId = employees.length > 0 
      ? Math.max(...employees.map(e => e.id)) 
      : 0;
    
    const employeeToAdd: Employee = {
      id: maxId + 1,
      name: newEmployee.name || '',
      location: newEmployee.location || '',
      department: newEmployee.department || '',
      isActive: newEmployee.isActive || true,
      hourlyRate: payType === 'hourly' ? newEmployee.hourlyRate : null,
      hoursWorked: payType === 'hourly' ? newEmployee.hoursWorked : null,
      salary: payType === 'salary' ? newEmployee.salary : null,
      calculatedPay,
      taxes,
      totalPay
    };
    
    // Add to employees list
    const updatedEmployees = [...employees, employeeToAdd];
    setEmployees(updatedEmployees);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEmployees));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    // Reset form and close modal
    resetForm();
    setIsAddingEmployee(false);
  };
  
  // Handle editing an employee
  const handleEditEmployee = (employee: Employee) => {
    setEditingEmployeeId(employee.id);
    setIsEditingEmployee(true);
    setNewEmployee({
      name: employee.name,
      location: employee.location,
      department: employee.department,
      isActive: employee.isActive,
      hourlyRate: employee.hourlyRate,
      hoursWorked: employee.hoursWorked,
      salary: employee.salary,
      calculatedPay: employee.calculatedPay,
      taxes: employee.taxes,
      totalPay: employee.totalPay
    });
    setPayType(employee.hourlyRate !== null ? 'hourly' : 'salary');
  };
  
  // Handle saving edited employee
  const handleSaveEdit = () => {
    // Validate form
    if (!newEmployee.name || newEmployee.name.trim() === '') {
      setFormError('Employee name is required');
      return;
    }
    
    if (payType === 'hourly') {
      if (!newEmployee.hourlyRate || newEmployee.hourlyRate <= 0) {
        setFormError('Hourly rate must be greater than 0');
        return;
      }
      
      if (!newEmployee.hoursWorked || newEmployee.hoursWorked <= 0) {
        setFormError('Hours worked must be greater than 0');
        return;
      }
      
      // Reset salary if switching to hourly
      newEmployee.salary = null;
    } else {
      if (!newEmployee.salary || newEmployee.salary <= 0) {
        setFormError('Salary must be greater than 0');
        return;
      }
      
      // Reset hourly fields if switching to salary
      newEmployee.hourlyRate = null;
      newEmployee.hoursWorked = null;
    }
    
    // Calculate pay
    const { calculatedPay, taxes, totalPay } = calculatePay(newEmployee);
    
    // Update the employee
    const updatedEmployees = employees.map(employee => {
      if (employee.id === editingEmployeeId) {
        return {
          ...employee,
          name: newEmployee.name || employee.name,
          location: newEmployee.location || employee.location,
          department: newEmployee.department || employee.department,
          isActive: newEmployee.isActive !== undefined ? newEmployee.isActive : employee.isActive,
          hourlyRate: newEmployee.hourlyRate,
          hoursWorked: newEmployee.hoursWorked,
          salary: newEmployee.salary,
          calculatedPay,
          taxes,
          totalPay
        };
      }
      return employee;
    });
    
    setEmployees(updatedEmployees);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEmployees));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    // Reset form and close modal
    resetForm();
    setIsEditingEmployee(false);
    setEditingEmployeeId(null);
  };
  
  // Handle deleting an employee
  const handleDeleteEmployee = (id: number) => {
    const updatedEmployees = employees.filter(employee => employee.id !== id);
    setEmployees(updatedEmployees);
    
    // Save to localStorage
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedEmployees));
    
    // Show success message briefly
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
    
    setShowDeleteConfirm(null);
  };
  
  // Handle manual save to localStorage
  const handleManualSave = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(employees));
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 3000);
  };
  
  // Calculate data for tables
  const getLocationData = () => {
    const locationData: { [key: string]: { count: number, payroll: number } } = {};
    
    filteredEmployees.forEach(employee => {
      if (employee.location) {
        if (!locationData[employee.location]) {
          locationData[employee.location] = { count: 0, payroll: 0 };
        }
        locationData[employee.location].count += 1;
        locationData[employee.location].payroll += employee.calculatedPay;
      } else {
        // Handle employees with no location
        if (!locationData['Unassigned']) {
          locationData['Unassigned'] = { count: 0, payroll: 0 };
        }
        locationData['Unassigned'].count += 1;
        locationData['Unassigned'].payroll += employee.calculatedPay;
      }
    });
    
    return Object.entries(locationData).map(([name, data]) => ({
      name,
      count: data.count,
      payroll: data.payroll
    }));
  };
  
  const getDepartmentData = () => {
    const departmentData: { [key: string]: { count: number, payroll: number } } = {};
    
    filteredEmployees.forEach(employee => {
      if (employee.department) {
        if (!departmentData[employee.department]) {
          departmentData[employee.department] = { count: 0, payroll: 0 };
        }
        departmentData[employee.department].count += 1;
        departmentData[employee.department].payroll += employee.calculatedPay;
      } else {
        // Handle employees with no department
        if (!departmentData['Unassigned']) {
          departmentData['Unassigned'] = { count: 0, payroll: 0 };
        }
        departmentData['Unassigned'].count += 1;
        departmentData['Unassigned'].payroll += employee.calculatedPay;
      }
    });
    
    return Object.entries(departmentData).map(([name, data]) => ({
      name,
      count: data.count,
      payroll: data.payroll
    }));
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Payroll Calculator</h1>
        <div className="flex gap-2">
          <button
            onClick={handleManualSave}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md flex items-center"
            title="Save all changes to browser storage"
          >
            <Save className="h-5 w-5 mr-1" />
            Save Changes
          </button>
          <button
            onClick={() => {
              resetForm();
              setIsAddingEmployee(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <Plus className="h-5 w-5 mr-1" />
            Add Employee
          </button>
        </div>
      </div>
      
      {/* Success message */}
      {saveSuccess && (
        <div className="mb-4 p-3 bg-green-100 border border-green-300 text-green-700 rounded-md flex items-center">
          <Check className="h-5 w-5 mr-2" />
          Changes saved successfully!
        </div>
      )}
      
      {/* Add/Edit Employee Modal */}
      {(isAddingEmployee || isEditingEmployee) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                {isAddingEmployee ? 'Add New Employee' : 'Edit Employee'}
              </h2>
              <button
                onClick={() => {
                  resetForm();
                  setIsAddingEmployee(false);
                  setIsEditingEmployee(false);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            {formError && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                {formError}
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Employee Name*
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newEmployee.name || ''}
                  onChange={(e) => setNewEmployee({ ...newEmployee, name: e.target.value })}
                  placeholder="e.g., Smith, John"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newEmployee.location || ''}
                  onChange={(e) => setNewEmployee({ ...newEmployee, location: e.target.value })}
                >
                  {availableLocations.map(location => (
                    <option key={location} value={location}>{location || 'Unassigned'}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newEmployee.department || ''}
                  onChange={(e) => setNewEmployee({ ...newEmployee, department: e.target.value })}
                >
                  {availableDepartments.map(department => (
                    <option key={department} value={department}>{department}</option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={newEmployee.isActive ? 'active' : 'inactive'}
                  onChange={(e) => setNewEmployee({ ...newEmployee, isActive: e.target.value === 'active' })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pay Type
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payType"
                      checked={payType === 'hourly'}
                      onChange={() => setPayType('hourly')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2">Hourly</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="payType"
                      checked={payType === 'salary'}
                      onChange={() => setPayType('salary')}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                    />
                    <span className="ml-2">Salary</span>
                  </label>
                </div>
              </div>
              
              {payType === 'hourly' ? (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hourly Rate*
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                        $
                      </span>
                      <input
                        type="number"
                        step="0.01"
                        className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        value={newEmployee.hourlyRate || ''}
                        onChange={(e) => setNewEmployee({ ...newEmployee, hourlyRate: parseFloat(e.target.value) || 0 })}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Hours Worked*
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newEmployee.hoursWorked || ''}
                      onChange={(e) => setNewEmployee({ ...newEmployee, hoursWorked: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                </>
              ) : (
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Salary*
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                      $
                    </span>
                    <input
                      type="number"
                      step="0.01"
                      className="flex-1 border border-gray-300 rounded-r-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={newEmployee.salary || ''}
                      onChange={(e) => setNewEmployee({ ...newEmployee, salary: parseFloat(e.target.value) || 0 })}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              )}
              
              <div className="md:col-span-2 bg-gray-50 p-4 rounded-lg">
                <h3 className="text-md font-semibold mb-2">Pay Calculation Preview</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Gross Pay</p>
                    <p className="text-lg font-bold">
                      ${payType === 'hourly' && newEmployee.hourlyRate && newEmployee.hoursWorked
                        ? (newEmployee.hourlyRate * newEmployee.hoursWorked).toFixed(2)
                        : payType === 'salary' && newEmployee.salary
                          ? newEmployee.salary.toFixed(2)
                          : '0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Taxes ({(TAX_RATE * 100).toFixed(2)}%)</p>
                    <p className="text-lg font-bold text-red-600">
                      ${payType === 'hourly' && newEmployee.hourlyRate && newEmployee.hoursWorked
                        ? (newEmployee.hourlyRate * newEmployee.hoursWorked * TAX_RATE).toFixed(2)
                        : payType === 'salary' && newEmployee.salary
                          ? (newEmployee.salary * TAX_RATE).toFixed(2)
                          : '0.00'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Benefits ({(BENEFITS_RATE * 100).toFixed(0)}%)</p>
                    <p className="text-lg font-bold text-green-600">
                      ${payType === 'hourly' && newEmployee.hourlyRate && newEmployee.hoursWorked
                        ? (newEmployee.hourlyRate * newEmployee.hoursWorked * BENEFITS_RATE).toFixed(2)
                        : payType === 'salary' && newEmployee.salary
                          ? (newEmployee.salary * BENEFITS_RATE).toFixed(2)
                          : '0.00'}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="flex justify-between">
                    <p className="text-md font-semibold">Total Pay:</p>
                    <p className="text-xl font-bold">
                      ${payType === 'hourly' && newEmployee.hourlyRate && newEmployee.hoursWorked
                        ? (newEmployee.hourlyRate * newEmployee.hoursWorked * (1 + BENEFITS_RATE - TAX_RATE)).toFixed(2)
                        : payType === 'salary' && newEmployee.salary
                          ? (newEmployee.salary * (1 + BENEFITS_RATE - TAX_RATE)).toFixed(2)
                          : '0.00'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <button
                onClick={() => {
                  resetForm();
                  setIsAddingEmployee(false);
                  setIsEditingEmployee(false);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-100"
              >
                Cancel
              </button>
              
              <button
                onClick={isAddingEmployee ? handleAddEmployee : handleSaveEdit}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
              >
                <Check className="h-5 w-5 mr-1" />
                {isAddingEmployee ? 'Add Employee' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6">
          <h2 className="text-xl font-bold">Payroll Overview</h2>
          <div className="mt-4 md:mt-0 flex flex-wrap gap-2">
            <select
              className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
            >
              <option value="all">All Locations</option>
              {availableLocations
                .filter(location => location) // Filter out empty location
                .map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
            </select>
            
            <select
              className="border rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={selectedDepartment}
              onChange={(e) => setSelectedDepartment(e.target.value)}
            >
              <option value="all">All Departments</option>
              {availableDepartments.map(department => (
                <option key={department} value={department}>{department}</option>
              ))}
            </select>
            
            <label className="flex items-center border rounded-md px-3 py-2 bg-white">
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm">Show Inactive</span>
            </label>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <DollarSign className="h-5 w-5 text-blue-600 mr-2" />
              <p className="text-sm text-gray-500">Total Gross Payroll</p>
            </div>
            <p className="text-2xl font-bold">${totalPayroll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Users className="h-5 w-5 text-green-600 mr-2" />
              <p className="text-sm text-gray-500">Active Employees</p>
            </div>
            <p className="text-2xl font-bold">{filteredEmployees.filter(e => e.isActive).length}</p>
          </div>
          
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="flex items-center mb-2">
              <Filter className="h-5 w-5 text-purple-600 mr-2" />
              <p className="text-sm text-gray-500">Average Pay</p>
            </div>
            <p className="text-2xl font-bold">
              ${filteredEmployees.length > 0 
                ? (totalPayroll / filteredEmployees.length).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) 
                : '0.00'}
            </p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Payroll by Location</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Employees
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Payroll
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Average Pay
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getLocationData().map((location, index) => (
                    <tr key={location.name} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4 border-b border-gray-200 font-medium">{location.name}</td>
                      <td className="py-2 px-4 border-b border-gray-200">{location.count}</td>
                      <td className="py-2 px-4 border-b border-gray-200">${location.payroll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-2 px-4 border-b border-gray-200">
                        ${(location.payroll / location.count).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-200">
                        {((location.payroll / totalPayroll) * 100).toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-4">Payroll by Department</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white">
                <thead>
                  <tr>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Department
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Employees
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Total Payroll
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Average Pay
                    </th>
                    <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      % of Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {getDepartmentData().map((department, index) => (
                    <tr key={department.name} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4 border-b border-gray-200 font-medium">{department.name}</td>
                      <td className="py-2 px-4 border-b border-gray-200">{department.count}</td>
                      <td className="py-2 px-4 border-b border-gray-200">${department.payroll.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="py-2 px-4 border-b border-gray-200">
                        ${(department.payroll / department.count).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-4 border-b border-gray-200">
                        {((department.payroll / totalPayroll) * 100).toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-bold mb-4">Employee Payroll</h2>
        
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Name
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Location
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Department
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Status
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Pay Type
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Rate/Salary
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Hours
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Gross Pay
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Taxes
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Total Pay
                </th>
                <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length > 0 ? (
                filteredEmployees.map((employee) => (
                  <tr key={employee.id} className={!employee.isActive ? 'bg-gray-100' : ''}>
                    <td className="py-2 px-4 border-b border-gray-200 font-medium">{employee.name}</td>
                    <td className="py-2 px-4 border-b border-gray-200">{employee.location || 'Unassigned'}</td>
                    <td className="py-2 px-4 border-b border-gray-200">{employee.department || 'Unassigned'}</td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        employee.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {employee.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      {employee.hourlyRate !== null ? 'Hourly' : 'Salary'}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      {employee.hourlyRate !== null 
                        ? `$${employee.hourlyRate.toFixed(2)}/hr` 
                        : employee.salary !== null 
                          ? `$${employee.salary.toFixed(2)}` 
                          : '-'}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      {employee.hoursWorked !== null ? employee.hoursWorked.toFixed(2) : '-'}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      ${employee.calculatedPay.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200 text-red-600">
                      ${employee.taxes.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200 font-semibold">
                      ${employee.totalPay.toFixed(2)}
                    </td>
                    <td className="py-2 px-4 border-b border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditEmployee(employee)}
                          className="text-blue-600 hover:text-blue-800"
                          title="Edit"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        
                        {showDeleteConfirm === employee.id ? (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleDeleteEmployee(employee.id)}
                              className="text-red-600 hover:text-red-800"
                              title="Confirm Delete"
                            >
                              <Check className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setShowDeleteConfirm(null)}
                              className="text-gray-600 hover:text-gray-800"
                              title="Cancel"
                            >
                              <X className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setShowDeleteConfirm(employee.id)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={11} className="py-4 px-4 text-center text-gray-500">
                    No employees found. Add one to get started.
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td colSpan={7} className="py-2 px-4 border-t border-gray-200 text-right">
                  Totals:
                </td>
                <td className="py-2 px-4 border-t border-gray-200">
                  ${totalPayroll.toFixed(2)}
                </td>
                <td className="py-2 px-4 border-t border-gray-200 text-red-600">
                  ${totalTaxes.toFixed(2)}
                </td>
                <td className="py-2 px-4 border-t border-gray-200">
                  ${totalPay.toFixed(2)}
                </td>
                <td className="py-2 px-4 border-t border-gray-200"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayrollCalculator;